import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const scope = () => { try { const u = JSON.parse(localStorage.getItem("pressops_user") || "{}"); return `${u.tenant_id || "-"}-${u.id || "-"}`; } catch { return "-"; } };
export const clearUnsaved = () => Object.keys(sessionStorage).filter((k) => k.startsWith("po-unsaved-")).forEach((k) => sessionStorage.removeItem(k));

/** Table rows with unsaved-edit protection: new Agyin drafts append without wiping edits, and unsaved rows survive navigation (sessionStorage) until saved or discarded. */
export function useDraftRows(rows, key, init = (r) => r, onDiscard) {
  const store = `po-unsaved-${scope()}-${key}`;
  const [data, setRaw] = useState([]);
  const [dirty, setDirty] = useState(false);
  const prev = useRef(null);
  const cur = useRef([]);
  cur.current = data;
  const persist = (n) => sessionStorage.setItem(store, JSON.stringify({ base: prev.current, data: n }));
  useEffect(() => {
    if (!rows) return;
    const next = rows.map(init);
    const p = prev.current;
    prev.current = next;
    if (p === null) {
      const s = JSON.parse(sessionStorage.getItem(store) || "null");
      // restore only if the server rows are unchanged since the edits were made (never apply stale edits over newer data)
      if (s && same(s.base, next)) { setRaw(s.data); setDirty(true); return; }
      sessionStorage.removeItem(store);
      setRaw(next);
      return;
    }
    if (same(next, p)) {  // same server rows re-delivered (config reload): keep unsaved edits unless they now match the server
      if (same(next, cur.current)) { sessionStorage.removeItem(store); setDirty(false); }
      return;
    }
    // only Agyin drafts are appended onto local edits; new saved server rows replace the local table
    const appended = next.length > p.length && p.every((r, i) => same(r, next[i])) && next.slice(p.length).every((r) => r._draft);
    if (appended) {
      setRaw((d) => { const n = [...d, ...next.slice(p.length)]; persist(n); return n; });
      setDirty(true);
    } else {
      sessionStorage.removeItem(store);
      setDirty(false);
      setRaw(next);
    }
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const setData = (v) => {
    setRaw((d) => { const n = typeof v === "function" ? v(d) : v; persist(n); return n; });
    setDirty(true);
  };
  const discard = () => { sessionStorage.removeItem(store); setDirty(false); setRaw((rows || []).filter((r) => !r._draft).map(init)); onDiscard?.(); };
  return { data, setData, dirty, discard };
}

export function UnsavedBar({ dirty, onDiscard, testId }) {
  if (!dirty) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`${testId}-unsaved`}>
      <span>Unsaved changes — kept until you save or discard, even if you leave this page.</span>
      <Button size="sm" variant="outline" className="h-7" data-testid={`${testId}-discard`} onClick={() => window.confirm("Discard all unsaved changes in this table?") && onDiscard()}>Discard changes</Button>
    </div>
  );
}
